import { zodResolver } from '@hookform/resolvers/zod';
import type { useMutation } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  EmrEnum,
  FacilityTypeEnum,
  HealthcareFacility,
  HospitalOwnershipEnum,
  SimpleHealthcareFacility,
} from '../../api';
import { FormFeedback } from '../../app/components/FormFeedback';
import { FormInputMaskField } from '../../app/components/FormInputMaskField';
import { FormLabel } from '../../app/components/FormLabel';
import { FormRadioField } from '../../app/components/FormRadioField';
import { FormTextareaField } from '../../app/components/FormTextareaField';
import { FormTextField } from '../../app/components/FormTextField';
import { SelectController } from '../../app/components/SelectController';
import { ServerErrorsAlert } from '../../app/components/ServerErrorsAlert';
import { FormSubmitContext } from '../../app/contexts/FormSubmitContext';
import { useBlockInvalidChar } from '../../app/hooks/block-invalid-char';
import { handleApiError } from '../../app/utils/errorhandling';
import {
  generateShowFieldErrors,
  generateZEnumFromEnum,
  nullToEmptyString,
  setFormErrorsFromApiErrors,
} from '../../utils/forms';
import { USStates } from '../../utils/local-data';

interface Props {
  initialValues?: HealthcareFacility;
  mutation: ReturnType<
    typeof useMutation<SimpleHealthcareFacility, unknown, HealthcareFacility, unknown>
  >;
  successMessage: string;
}

const validationSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  street_address: z.string().min(1, { message: 'Street address is required' }),
  city: z.string().min(1, { message: 'City is required' }),
  state: generateZEnumFromEnum(USStates),
  zip_code: z.string().length(5),
  phone_number: z.string().min(10).max(20),
  hospital_daily_census: z.union([
    z.null(),
    z
      .string()
      .length(0)
      .transform(() => null),
    z.coerce.number().positive().optional(),
  ]),
  er_daily_census: z.union([
    z.null(),
    z
      .string()
      .length(0)
      .transform(() => null),
    z.coerce.number().positive().optional(),
  ]),
  icu_daily_census: z.union([
    z.null(),
    z
      .string()
      .length(0)
      .transform(() => null),
    z.coerce.number().positive().optional(),
  ]),
  facility_type: z
    .union([z.null(), z.string().length(0), generateZEnumFromEnum(FacilityTypeEnum)])
    .optional()
    .transform((val) => val || '')
    .pipe(z.union([z.string().length(0), generateZEnumFromEnum(FacilityTypeEnum)]).optional()),
  emr: z
    .union([z.null(), z.string().length(0), generateZEnumFromEnum(EmrEnum)])
    .optional()
    .transform((val) => val || '')
    .pipe(z.union([z.string().length(0), generateZEnumFromEnum(EmrEnum)]).optional()),
  hospital_ownership: z
    .union([z.null(), z.string().length(0), generateZEnumFromEnum(HospitalOwnershipEnum)])
    .optional()
    .transform((val) => val || '')
    .pipe(z.union([z.string().length(0), generateZEnumFromEnum(HospitalOwnershipEnum)]).optional()),
  number_of_beds: z.union([
    z.null(),
    z
      .string()
      .length(0)
      .transform(() => null),
    z.coerce.number().positive().optional(),
  ]),
  or_rooms: z.union([
    z.null(),
    z
      .string()
      .length(0)
      .transform(() => null),
    z.coerce.number().positive().optional(),
  ]),
  icu_open: z
    .string()
    .transform((value) => {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return null;
    })
    .pipe(z.boolean().nullable())
    .nullable(),
  required_procedures: z.string().nullable().transform(nullToEmptyString),
  extra_info: z.string().optional(),
});

export const FacilityForm = ({ initialValues, mutation, successMessage }: Props) => {
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    name?: boolean;
    facility_type?: boolean;
    street_address?: boolean;
    city?: boolean;
    state?: boolean;
    zip_code?: boolean;
    phone_number?: boolean;
    hospital_daily_census?: boolean;
    er_daily_census?: boolean;
    icu_daily_census?: boolean;
    emr?: boolean;
    hospital_ownership?: boolean;
    number_of_beds?: boolean;
    or_rooms?: boolean;
    icu_open?: boolean;
    required_procedures?: boolean;
  }>({});
  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
    setError,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const blockInvalidChar = useBlockInvalidChar(/[\d]/);

  useEffect(() => {
    if (initialValues && !isEmpty(initialValues)) {
      reset({ ...initialValues, icu_open: String(initialValues.icu_open ?? null) });
    }
  }, [initialValues, reset]);

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data: HealthcareFacility) => {
    try {
      await mutation.mutateAsync(data);
      toast.success(successMessage);
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} lg={6}>
          <FormTextField
            name="name"
            label="Name"
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            required={true}
          />
          <Form.Group className="mb-3">
            <FormLabel>Facility Type</FormLabel>
            <SelectController
              name="facility_type"
              control={control}
              options={Object.entries(window.FACILITY_TYPES).map(([value, label]) => ({
                value,
                label,
              }))}
              isMulti={false}
              placeholder="Choose one"
              isClearable={true}
              onChange={() => {
                setShowServerFieldErrors((prev) => omit(prev, 'facility_type'));
              }}
            />
            <FormFeedback
              clientError={errors.facility_type}
              serverErrors={
                showServerFieldErrors.facility_type ? serverErrors?.body?.facility_type : null
              }
            />
          </Form.Group>
          <FormInputMaskField
            name="phone_number"
            label="Phone Number"
            mask="(___) ___-____"
            replacement={{ _: /\d/ }}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            required={true}
          />
          <FormTextField
            name="street_address"
            label="Street Address"
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            required={true}
          />
          <FormTextField
            name="city"
            label="City"
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            required={true}
          />
          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <FormLabel required>State</FormLabel>
                <SelectController
                  name="state"
                  control={control}
                  options={Object.entries(USStates).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  isMulti={false}
                  placeholder="Choose one"
                  isClearable={false}
                  onChange={() => {
                    setShowServerFieldErrors((prev) => omit(prev, 'state'));
                  }}
                />
                <FormFeedback
                  clientError={errors.state}
                  serverErrors={showServerFieldErrors.state ? serverErrors?.body?.state : null}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <FormTextField
                name="zip_code"
                label="Zip Code"
                onKeyDown={blockInvalidChar}
                maxLength={5}
                showServerFieldErrors={showServerFieldErrors}
                setShowServerFieldErrors={setShowServerFieldErrors}
                errors={errors}
                register={register}
                required={true}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={6}>
          <FormTextareaField
            name="extra_info"
            label="Facility Details"
            rows={4}
            placeholder="Examples: staffing model, patient encounters, specialty support, etc."
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
          />
          <FormTextareaField
            name="required_procedures"
            label="Required Procedures"
            errors={errors}
            register={register}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
          />
          <Form.Group className="mb-3">
            <FormLabel>EMR</FormLabel>
            <SelectController
              name="emr"
              control={control}
              options={Object.entries(window.EMR_CHOICES).map(([value, label]) => ({
                value,
                label,
              }))}
              isMulti={false}
              placeholder="Choose one"
              onChange={() => {
                setShowServerFieldErrors((prev) => omit(prev, 'emr'));
              }}
            />
            <FormFeedback
              clientError={errors.emr}
              serverErrors={showServerFieldErrors.emr ? serverErrors?.body?.emr : null}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <FormLabel>Hospital Ownership</FormLabel>
            <SelectController
              name="hospital_ownership"
              control={control}
              options={Object.entries(window.HOSPITAL_OWNERSHIP_CHOICES).map(([value, label]) => ({
                value,
                label,
              }))}
              isMulti={false}
              placeholder="Choose one"
              onChange={() => {
                setShowServerFieldErrors((prev) => omit(prev, 'hospital_ownership'));
              }}
            />
            <FormFeedback
              clientError={errors.hospital_ownership}
              serverErrors={
                showServerFieldErrors.hospital_ownership
                  ? serverErrors?.body?.hospital_ownership
                  : null
              }
            />
          </Form.Group>
          <FormTextField
            name="hospital_daily_census"
            label="Hospital Daily Census"
            type="number"
            min={0}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            onKeyDown={blockInvalidChar}
          />
          <FormTextField
            name="er_daily_census"
            label="ER Daily Census"
            type="number"
            min={0}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            onKeyDown={blockInvalidChar}
          />
          <FormTextField
            name="icu_daily_census"
            label="ICU Daily Census"
            type="number"
            min={0}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            onKeyDown={blockInvalidChar}
          />
          <FormTextField
            name="number_of_beds"
            label="Number of Beds"
            type="number"
            min={0}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            onKeyDown={blockInvalidChar}
          />
          <FormTextField
            name="or_rooms"
            label="OR Rooms"
            type="number"
            min={0}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
            onKeyDown={blockInvalidChar}
          />
          <FormRadioField name="icu_open" label="ICU Open" errors={errors} register={register} />
        </Col>
      </Row>
      <ServerErrorsAlert
        errors={serverErrors}
        showErrors={showGlobalServerErrors}
        clearErrors={handleClearErrorsAlert}
      />
      <div className="d-flex justify-content-end mb-3">
        <Button
          type="submit"
          onClick={handleSubmitClick}
          disabled={mutation.isLoading || !isSubmitEnabled}
        >
          Save
        </Button>
      </div>
    </Form>
  );
};
